/**
 * Booking price calculation — ONE definition, used by both the modal and the
 * /book page.
 *
 * Why this file exists: BookingModal.js carried the same arithmetic twice —
 * once to render the summary the customer reads, and again inside handleSubmit
 * to build the amount sent to Stripe. The two copies were not identical:
 *
 *   - the display copy priced a multi-quantity add-on as `price * (qty || 0)`
 *     and every other add-on as a flat `price`;
 *   - the submit copy priced EVERY add-on as `price * (qty || 1)`.
 *
 * They agree on the happy path (toggling a multi-qty add-on always writes a
 * quantity >= 1). They diverge when a stale quantity survives a tour switch —
 * add-on `max_quantity` is per-tour, so an add-on that allowed 3 on tour A and
 * only 1 on tour B keeps `addOnQuantities[id] = 3`, and the guest would have
 * been CHARGED more than the page showed them.
 *
 * This module standardises on the DISPLAY semantics: the customer is never
 * charged more than the total they agreed to.
 */

import { isCouponLive } from './couponRules';
export { isCouponLive } from './couponRules';

/**
 * Dollars for display: "$104.99" → "104.99", "$105.00" → "105".
 *
 * The tour cards used to Math.round() their prices while the summary and Stripe
 * kept the cents, so a $104.99 tour read "$105" on one step and "$104.99" on
 * the next — and its 20%-off price read "$84" and then "$83.99". Every price a
 * guest sees now goes through this one formatter (caller adds the "$").
 */
export function formatMoney(n) {
  const num = Number(n);
  if (!Number.isFinite(num)) return '0';
  return num.toFixed(2).replace(/\.00$/, '');
}

/** "20% off" / "$50 off" — how a coupon is announced on cards, bars and boxes. */
export function couponAmountLabel(coupon) {
  if (!coupon) return '';
  const value = parseFloat(coupon.discount_value) || 0;
  return coupon.discount_type === 'percentage' ? `${value}% off` : `$${formatMoney(value)} off`;
}

/**
 * "2026-09-30" (or anything starting with it) → local Date at midnight, or null.
 * Local, not UTC: `new Date('2026-09-30')` is UTC midnight, which in New York
 * is still the evening of the 29th — every "through Sep 30" would print "Sep 29".
 */
export function parseCouponDate(value) {
  if (!value) return null;
  const m = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : null;
}

/**
 * The coupon behind the site-wide promo: the row whose code matches
 * site_settings.main_coupon_code (case-insensitive) AND that is live today.
 * The promo bar, the /book side box and the calendar all resolve it through
 * this one function, so they can never advertise three different offers.
 */
export function findMainCoupon(coupons, mainCode) {
  const code = String(mainCode || '').trim().toUpperCase();
  if (!code || !Array.isArray(coupons)) return null;
  const row = coupons.find((c) => c && String(c.code || '').toUpperCase() === code);
  return row && isCouponLive(row) ? row : null;
}

/**
 * Sum the selected add-ons.
 *
 * An add-on is quantity-based when its own `max_quantity > 1`; otherwise it is
 * a single flat-price item. Deriving this from the add-on row (instead of a
 * separately-maintained id list) is what keeps a stale quantity from leaking
 * into the total.
 *
 * @param {Array}  addOns      Normalised add-on rows for the current tour.
 * @param {Array}  selectedIds Ids the guest ticked (`formData.addOns`).
 * @param {Object} quantities  `formData.addOnQuantities`.
 */
export function calculateAddOnsTotal(addOns = [], selectedIds = [], quantities = {}) {
  return addOns
    .filter((addon) => selectedIds.includes(addon.id))
    .reduce((sum, addon) => {
      const price = parseFloat(addon.price) || 0;
      const isMultiQty = Number(addon.max_quantity) > 1;
      if (isMultiQty) {
        const qty = quantities[addon.id] || 0;
        return sum + price * qty;
      }
      return sum + price;
    }, 0);
}

/**
 * Per-carriage booking (processing) fee, or 0 when the tour has none.
 * `booking_fee_enabled` arrives from SQLite as 0/1, hence the Boolean() cast.
 */
export function calculateBookingFee(tour, carriages = 1) {
  const enabled = tour && Boolean(tour.booking_fee_enabled) && tour.booking_fee > 0;
  if (!enabled) return 0;
  return parseFloat(tour.booking_fee) * (parseInt(carriages, 10) || 1);
}

/**
 * Work out what a coupon is worth against this basket.
 *
 * Mirrors the backend's rules so we never display a discount the server will
 * refuse: tour restriction, tour-DATE range (not purchase date), and the
 * `applies_to` base.
 *
 * `all` includes the booking fee, matching authoritative server pricing.
 *
 * @returns {{discount:number, mismatchReason:string|null}}
 */
export function calculateCouponDiscount(coupon, { tourId, date, tourTotal, addOnsTotal, bookingFee = 0 }) {
  if (!coupon) return { discount: 0, mismatchReason: null };

  const subtotal = tourTotal + addOnsTotal;

  const tourIds = Array.isArray(coupon.applies_to_tour_ids) ? coupon.applies_to_tour_ids : [];
  const tourOk = tourIds.length === 0 || tourIds.includes(parseInt(tourId, 10));
  if (!tourOk) {
    return { discount: 0, mismatchReason: 'This coupon does not apply to the selected tour.' };
  }

  let dateOk = true;
  if (date) {
    if (coupon.booking_date_from && date < coupon.booking_date_from) dateOk = false;
    if (coupon.booking_date_until && date > coupon.booking_date_until) dateOk = false;
  }
  if (!dateOk) {
    return {
      discount: 0,
      mismatchReason: `This coupon is only valid for tour dates ${coupon.booking_date_from || ''} → ${coupon.booking_date_until || ''}.`,
    };
  }

  const appliesTo = (coupon.applies_to || 'all').toLowerCase();
  let discountBase;
  if (appliesTo === 'tours') {
    discountBase = tourTotal;
  } else if (appliesTo === 'addons' || appliesTo === 'add_ons' || appliesTo === 'add-ons') {
    discountBase = addOnsTotal;
  } else {
    // 'all' means everything the guest pays, INCLUDING the booking fee — that is
    // what the worker discounts (booking-api calculateTotalPrice), and the worker
    // is the one that prices the real booking. Leaving the fee out here made the
    // page quote a smaller discount than the receipt, on exactly the fee-bearing
    // tours a holiday sale would target.
    discountBase = subtotal + bookingFee;
  }

  let discount =
    coupon.discount_type === 'percentage'
      ? (discountBase * coupon.discount_value) / 100
      : parseFloat(coupon.discount_value || 0);

  discount = Math.min(discount, discountBase);

  return { discount, mismatchReason: null };
}

/**
 * The complete basket. Everything the UI shows and everything Stripe charges
 * comes out of this one call.
 *
 * @returns {{
 *   baseTourPrice:number, tourPriceWithCarriages:number, addOnsTotal:number,
 *   subtotal:number, bookingFee:number, couponDiscount:number,
 *   couponMismatchReason:string|null, totalPrice:number, finalAmount:number
 * }}
 */
export function calculateBookingTotal({
  tour,
  carriages = 1,
  addOns = [],
  selectedAddOnIds = [],
  addOnQuantities = {},
  coupon = null,
  tourId,
  date,
}) {
  const carriageCount = parseInt(carriages, 10) || 1;

  const baseTourPrice = tour ? parseFloat(tour.price) || 0 : 0;
  const tourPriceWithCarriages = baseTourPrice * carriageCount;
  const addOnsTotal = calculateAddOnsTotal(addOns, selectedAddOnIds, addOnQuantities);
  const subtotal = tourPriceWithCarriages + addOnsTotal;

  const bookingFee = calculateBookingFee(tour, carriageCount);

  const { discount: couponDiscount, mismatchReason: couponMismatchReason } =
    calculateCouponDiscount(coupon, {
      tourId: tourId !== undefined ? tourId : tour && tour.id,
      date,
      tourTotal: tourPriceWithCarriages,
      addOnsTotal,
      bookingFee,
    });

  const totalPrice = subtotal - couponDiscount + bookingFee;

  return {
    baseTourPrice,
    tourPriceWithCarriages,
    addOnsTotal,
    subtotal,
    bookingFee,
    couponDiscount,
    couponMismatchReason,
    totalPrice,
    // Stripe rejects zero-value sessions; floor at one cent.
    finalAmount: Math.max(0.01, totalPrice),
  };
}
